import React, { useCallback } from "react";
import styles from "./users-deactivation-settings-page.module.scss";
import { Button } from "@whatslly/ui-components/v2/Button";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import classnames from "classnames";
import { useDependencyInjector } from "@whatslly/commons/di";
import { UsersDeactivationSettingsStore } from "../../stores/UsersDeactivationSettingsStore";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { CrmComponentsErrorBox } from "@whatslly/ui-components/v2/CrmComponentsErrorBox";
import { LOADER_IMG } from "@whatslly/commons/constants";
import type { IntervalOptionType } from "../../types/IntervalOptionType";
import { INTERVAL_OPTIONS } from "../../stores/IntervalOptions";

export const UsersDeactivationSettingsPage: React.FC = observer(() => {
  const deactivationSettingsStore = useDependencyInjector(UsersDeactivationSettingsStore);
  const { t } = useTranslation();

  const runNow = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("usersDeactivationPage.modal_start_now"),
    });
    await deactivationSettingsStore.runNow();
    modalService.openModal(Toast, {
      text: t("usersDeactivationPage.modal_finish_now"),
    });
  }, [deactivationSettingsStore, t]);

  const saveSettings = useCallback(async () => {
    modalService.openModal(Toast, {
      text: t("usersDeactivationPage.modal_saved_interval"),
    });
    await deactivationSettingsStore.saveSettings();
  }, [deactivationSettingsStore, t]);

  return (
    <div className={styles.page}>
      <div className={styles.header} data-testid={"user deactivation"}>
        {t("usersDeactivationPage.header")}
      </div>
      {deactivationSettingsStore.init.case({
        pending: () => (
          <div className={styles.loading}>
            <img alt="tuvis loader" src={LOADER_IMG} />
            <div>{t("usersDeactivationPage.loading")}</div>
          </div>
        ),
        fulfilled: () => (
          <>
            <div className={styles.desc}>{t("usersDeactivationPage.desc")}</div>
            <div className={styles.select}>
              <Select<IntervalOptionType>
                disabled={deactivationSettingsStore.isSaving}
                options={INTERVAL_OPTIONS}
                value={deactivationSettingsStore.selectedOption}
                onChange={(item: IntervalOptionType) => deactivationSettingsStore.setSelectedOption(item)}
              />
            </div>
            <div className={styles.runNowSection}>
              <div>{deactivationSettingsStore.selectedOption?.description}</div>
              <div onClick={runNow} className={classnames(styles.runNowButton, { [styles.disabled]: deactivationSettingsStore.isRunning })}>
                {t("usersDeactivationPage.runNow")}
              </div>
            </div>
            <Button
              isSubmitting={deactivationSettingsStore.isSaving}
              disabled={deactivationSettingsStore.isSaving}
              testId={`save-settings`}
              paddingInline="small"
              fontSize="small"
              onClick={saveSettings}
            >
              {t("usersDeactivationPage.saveSettings")}
            </Button>
            {(deactivationSettingsStore.previousRun || deactivationSettingsStore.nextRun) && (
              <div className={styles.scheduleSection}>
                {deactivationSettingsStore.previousRun && (
                  <div>
                    {t("usersDeactivationPage.previousRun")}: {deactivationSettingsStore.previousRun}
                  </div>
                )}
                {deactivationSettingsStore.nextRun && (
                  <div>
                    {t("usersDeactivationPage.nextRun")}: {deactivationSettingsStore.nextRun}
                  </div>
                )}
              </div>
            )}
          </>
        ),
        rejected: (error: Error) => <CrmComponentsErrorBox error={error.message} />,
      })}
    </div>
  );
});
